import { joinCatalogCards } from "@openrift/shared/catalog-join";
import { promosContract } from "@openrift/shared/contracts/promos";
import type { PromosListResponse } from "@openrift/shared/types/api/catalog";
import type { Card, Printing } from "@openrift/shared/types/catalog";
import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";

import { promosKeys } from "@/features/cards/lib/cards-query-keys";
import { serverCacheKeys } from "@/lib/query-keys";
import { serverCache } from "@/lib/server-cache";
import { withCookies } from "@/lib/server-fns/middleware";
import { apiOrpcClient } from "@/lib/server-fns/orpc-client";

const fetchPromoList = createServerFn({ method: "GET" })
  .validator((language: string) => language)
  .middleware([withCookies])
  .handler(({ context, data: language }): Promise<PromosListResponse> =>
    serverCache.query({
      queryKey: serverCacheKeys.promos(language),
      queryFn: () => apiOrpcClient(promosContract, context.cookie).list({ language }),
    }),
  );

interface EnrichedPromoList {
  channels: PromosListResponse["channels"];
  printings: Printing[];
  cards: Record<string, PromosListResponse["cards"][string] & Pick<Card, "upcomingBans">>;
  sets: PromosListResponse["sets"];
  languages: string[];
  languageCounts: PromosListResponse["languageCounts"];
}

function enrichPromoList(response: PromosListResponse): EnrichedPromoList {
  const setSlugById = new Map(response.sets.map((set) => [set.id, set.slug]));
  const cards = joinCatalogCards(response.cards);
  const printings: Printing[] = response.printings.flatMap((p) => {
    const card = cards[p.cardId];
    return card ? [{ ...p, setSlug: setSlugById.get(p.setId) ?? "", setReleased: true, card }] : [];
  });
  return {
    channels: response.channels,
    printings,
    cards,
    sets: response.sets,
    languages: response.languages,
    languageCounts: response.languageCounts,
  };
}

export function publicPromoListQueryOptions(language: string) {
  return queryOptions({
    queryKey: promosKeys.forLanguage(language),
    queryFn: () => fetchPromoList({ data: language }),
    staleTime: 5 * 60 * 1000,
    select: enrichPromoList,
  });
}
